import { useEffect, useRef, useState } from 'react';
import type { CoffeeSubmission } from '../../lib/coffee-project';

interface Dot {
  id: string;
  x: number;
  y: number;
  status: string;
  projectNumber: number;
}

interface Connection {
  from: string;
  to: string;
}

// Deterministic pseudo-random based on project number so dots stay put between renders
function seededPosition(num: number, w: number, h: number): { x: number; y: number } {
  const seed = num * 9301 + 49297;
  const r1 = (seed % 233280) / 233280;
  const r2 = ((seed * 7) % 233280) / 233280;
  const padding = 40;
  return {
    x: padding + r1 * (w - padding * 2),
    y: padding + r2 * (h - padding * 2),
  };
}

export default function Constellation({ submissions }: { submissions: CoffeeSubmission[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 800, h: 400 });
  const [newDotId, setNewDotId] = useState<string | null>(null);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDims({ w: rect.width, h: Math.max(400, rect.height) });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Track newest submission for the "has entered" animation
  useEffect(() => {
    if (submissions.length === 0) return;
    const latest = submissions[submissions.length - 1];
    setNewDotId(latest.id);
    const t = setTimeout(() => setNewDotId(null), 2000);
    return () => clearTimeout(t);
  }, [submissions.length]);

  const dots: Dot[] = submissions.map((s) => ({
    id: s.id,
    ...seededPosition(s.project_number, dims.w, dims.h),
    status: s.status,
    projectNumber: s.project_number,
  }));

  const happened = submissions.filter((s) => s.status === 'happened');
  const connections: Connection[] = happened.slice(0, 20).map((s, i) => ({
    from: s.id,
    to: happened[(i + 1) % Math.max(1, happened.length)]?.id ?? s.id,
  }));

  return (
    <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
      <div ref={containerRef} className="absolute inset-0">
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
          {connections.map((c, i) => {
            const from = dots.find((d) => d.id === c.from);
            const to = dots.find((d) => d.id === c.to);
            if (!from || !to) return null;
            return (
              <line
                key={i}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke="#fda36b"
                strokeWidth="1"
                opacity="0.3"
                className="animate-[fadeIn_1s_ease]"
              />
            );
          })}
        </svg>

        {dots.map((dot) => {
          const isHappened = dot.status === 'happened';
          const isNew = dot.id === newDotId;
          return (
            <div
              key={dot.id}
              className={`absolute rounded-full transition-all duration-700 ${
                isHappened
                  ? 'bg-[#fda36b] w-3 h-3 shadow-[0_0_12px_rgba(253,163,107,0.6)]'
                  : 'bg-white/30 w-2 h-2 hover:bg-white/60'
              } ${isNew ? 'animate-ping' : ''}`}
              style={{
                left: `${dot.x}px`,
                top: `${dot.y}px`,
                transform: 'translate(-50%, -50%)',
              }}
              title={`Coffee #${dot.projectNumber}`}
            />
          );
        })}

        {newDotId && (
          <div
            className="absolute text-[#fda36b] text-sm font-mono font-medium animate-[fadeIn_0.5s_ease] pointer-events-none"
            style={{
              left: dots.find((d) => d.id === newDotId)?.x ?? 0,
              top: (dots.find((d) => d.id === newDotId)?.y ?? 0) - 20,
              transform: 'translate(-50%, -100%)',
            }}
          >
            Coffee #{dots.find((d) => d.id === newDotId)?.projectNumber} has entered the experiment.
          </div>
        )}
      </div>

      {submissions.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-white/30 text-lg">Every coffee starts with a single dot.</p>
        </div>
      )}
    </div>
  );
}
